import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { AddVehicleForm } from "@/components/garage/AddVehicleForm";
import { GarageVehicleRow } from "@/components/garage/GarageVehicleRow";

const vKey = (v) => `${(v.make || "").toLowerCase()}|${(v.model || "").toLowerCase()}|${v.year}|${(v.rego || "").toUpperCase()}`;

export const SavedVehicles = ({ vehicles = [], bookings = [], bookingsByVehicle = {}, onChanged, userEmail = "" }) => {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openLog, setOpenLog] = useState(null);

  const suggestions = useMemo(() => {
    const saved = new Set(vehicles.map(vKey));
    const seen = new Set();
    const out = [];
    for (const b of bookings) {
      if (!b.vehicle_make) continue;
      const v = { make: b.vehicle_make, model: b.vehicle_model || "", year: b.vehicle_year, rego: b.vehicle_rego || "", state: b.vehicle_state || "TAS" };
      const k = vKey(v);
      if (saved.has(k) || seen.has(k)) continue;
      seen.add(k);
      out.push(v);
    }
    return out.slice(0, 5);
  }, [vehicles, bookings]);

  const save = async (v) => {
    setBusy(true);
    try {
      await api.post("/customer/vehicles", { make: v.make, model: v.model, year: Number(v.year), rego: v.rego || null, state: v.state || "TAS" });
      toast.success("Vehicle saved to your garage");
      setAdding(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save vehicle");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/customer/vehicles/${id}`).catch(() => {});
    onChanged?.();
  };

  return (
    <div data-testid="saved-vehicles-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display uppercase text-2xl tracking-tight text-white">My vehicles</h2>
        <button data-testid="add-vehicle-toggle" onClick={() => setAdding(!adding)}
          className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-300 text-xs font-mono uppercase tracking-widest hover:border-brand-red hover:text-brand-red">
          <Plus className="h-3.5 w-3.5" /> {adding ? "Close" : "Add vehicle"}
        </button>
      </div>

      {adding && <AddVehicleForm onSave={save} busy={busy} />}

      <div className="mt-4 space-y-px bg-zinc-900 border border-zinc-900" data-testid="saved-vehicles-list">
        {vehicles.length === 0 && !adding && (
          <div className="bg-zinc-950 p-6 text-center text-sm text-zinc-500">
            No saved vehicles yet — add one and it'll be a one-tap pick next time you book.
          </div>
        )}
        {vehicles.map((v) => (
          <GarageVehicleRow
            key={v.id}
            v={v}
            log={bookingsByVehicle[v.id] || []}
            isOpen={openLog === v.id}
            onToggleLog={() => setOpenLog(openLog === v.id ? null : v.id)}
            onRemove={() => remove(v.id)}
            userEmail={userEmail}
          />
        ))}
      </div>

      {suggestions.length > 0 && (
        <div className="mt-3" data-testid="vehicle-suggestions">
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">From your past bookings — tap to save</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {suggestions.map((v) => (
              <button key={vKey(v)} data-testid="vehicle-suggestion-chip" disabled={busy} onClick={() => save(v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-700 text-xs text-zinc-300 hover:border-brand-red hover:text-brand-red transition-colors disabled:opacity-50">
                <Plus className="h-3 w-3" /> {v.year} {v.make} {v.model}{v.rego ? ` · ${v.rego}` : ""}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
